/**
 * Helper functions for building the Jira ticket content.
 */
import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';

import { plural } from '../../../../../../shared/helpers/plural';
import { Category, COUNT_MAX_FE } from '../../../../../../shared/issuesV2';
import { NO_SAFE_VERSION, UNABLE_TO_CALCULATE } from '../../../../../../shared/vulns/remediation';
import { getIssueTypeLabel } from '../../../../../helpers/issuesV2';
import type {
  Issue,
  LicensingIssue,
  MalwareVulnIssue,
  QualityIssue,
  Scope,
  Source,
  VulnerabilityIssue,
  VulnIssue,
} from '../../../../../../shared/issuesV2';
import type { User } from '../../../../../types';

import { getJiraTicketAffectedProjectURLForScope } from './utils';

type TicketContent = {
  Title: `[FOSSA] ${string}`;
  Link: `[${string}](${string})`;
  H2: `## ${string}\n\n`;
  H3: `### ${string}\n\n`;
  Hr: `---\n`;
  Line: `${string}\n`;
  Bold: `**${string}**`;
  Italic: `_${string}_`;
  IssueSource: `${string} (${string})` | string;
  Panel: `{panel:title=${string}}\n${string}\n{panel}`;
};

const markdown = {
  title: (title: string): TicketContent['Title'] => `[FOSSA] ${title}`,
  link: (anchor: string, url: string): TicketContent['Link'] => `[${anchor}](${url})`,
  h2: (text: string): TicketContent['H2'] => `## ${text}\n\n`,
  h3: (text: string): TicketContent['H3'] => `### ${text}\n\n`,
  bold: (text: string): TicketContent['Bold'] => `**${text}**`,
  italic: (text: string): TicketContent['Italic'] => `_${text}_`,
  hr: (): TicketContent['Hr'] => `---\n`,
  line: (text = ''): TicketContent['Line'] => `${text}\n`,
  lines: (text: string, count: number): string => `${markdown.line(text)}${_.range(0, count - 1).map(() => '').join('\n')}`,
  issueSource: (source: Issue['source']): TicketContent['IssueSource'] => `${source.name}${source.version ? ` (${source.version})` : ''}`,
  issueHeading: () => markdown.h3('Issue'),
  //  Renders an info panel in Jira, using Atlassian's funky markdown.
  panel: (title: string, content: string): TicketContent['Panel'] => `{panel:title=${title}}\n${content}\n{panel}`,
};

/**
 * Displays the warning when the user has more than 50 issues selected for export.
 *
 * @param {number} selectedIssuesCount -Count of selected issues.
 */
const FEMaxCountWarning = (selectedIssuesCount: number) =>
  markdown.lines(markdown.bold(`There are ${selectedIssuesCount} selected issues. Ticket content will just include information of the first ${COUNT_MAX_FE} issues.`), 3);

/**
 * Helper for building the title of the exporting ticket.
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 * @param {number} selectedIssuesCount - The number of issues selected. If on issue details, this is always 1.
 */
export const Title = (issues: Issue[], selectedIssuesCount = 1): TicketContent['Title'] => {
  //  Get a count of the various packages where the issues come from.
  const sourcesCount = Object.keys(_.groupBy(issues, issue => issue.source.id)).length;
  if (sourcesCount > 1) {
    const title = markdown.title(`${selectedIssuesCount} Issues found`);
    //  If we have everything selected, we can include the number of dependencies.
    if (selectedIssuesCount === issues.length) {
      return `${title} in ${sourcesCount} dependencies`;
    }
    return title;
  }
  const issue = _.head(issues) as Issue;
  return markdown.title(`Issue with ${markdown.issueSource(issue.source)} - ${getIssueTypeLabel(issue.type)}`);
};

/**
 * Determines the link back to the issue in FOSSA, based on the current URL.
 * If we are on the issue details page, there is no need to append
 * the issue ID to the URL, but on the issues inbox we should.
 *
 * @param {number} issueId - The ID to the issue.
 * @param {string} url - The URL to the issue.
 */
const ViewOnFossaLink = (
  issueId: number,
  url: string,
  includeIssueId = false,
): TicketContent['Link'] => {
  const anchorText = `View ${includeIssueId ? `issue #${issueId}` : 'issue'} on FOSSA`;

  return markdown.link(anchorText, url);
};

/**
 * Renders a list of ViewOnFossaLinks
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for.
 */
const ViewOnFossaLinks = (issues: Issue[]): string => {
  if (issues.length === 1) {
    const issue = _.head(issues) as Issue;
    return markdown.lines(ViewOnFossaLink(issue.id, issue.url), 3);
  }

  return [
    ...issues.map(issue => `- ${ViewOnFossaLink(issue.id, issue.url, true)}`),
    markdown.line(),
  ].join(markdown.line());
};

/**
 * Helper for building the top part of the content section of an exporting ticket.
 *
 * @param {Issue} issue - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 */
const Header = (issue: Issue): string =>
  markdown.h2(`${getIssueTypeLabel(issue.type)} - ${markdown.issueSource(issue.source)}`);

/**
 * Renders the Package URL section, assuming the package
 * has a URL which can be rendered.
 *
 * @param {Souce} source - Issue source.
 */
const PackageURL = (source?: Source): string => {
  if (!source?.url) {
    return '';
  }
  return markdown.h3('Package URL') + markdown.lines(markdown.link(source.name, source.url), 3);
};

/**
 * Takes a list of issues and returns the Affected Projects section.
 * Removes duplicate project entries and renders each project as
 * a link on a new link.
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 * @param {Category} category - The category of the issues.
 */
const ProjectLinks = (issues: Issue[], location: Location, scope: Scope, category: Category): string => {
  const heading = markdown.h3('Affected Projects');
  const projectLines: string[] = [];

  // Check if any issues have projects before proceeding
  const hasProjects = issues.some(issue => issue.projects.length > 0);
  if (!hasProjects) {
    return '';
  }

  issues.forEach(issue => {
    issue.projects.forEach(project => {
      const url = getJiraTicketAffectedProjectURLForScope({
        affectedProject: {
          id: project.id,
          revisionId: project.revisionId ?? '',
          title: project.title,
          status: project.status,
          branch: project.defaultBranch ?? '',
          type: null,
          customTicketUrl: null,
          jiraTicketId: null,
          jiraTicketURL: null,
        },
        scope,
        issueCategory: category,
        issueId: issue.id,
        revisionScanId: project.revisionScanId,
      });
      const fullURL = `${location.origin}${url}`;
      const link = markdown.link(`${project.title}: Issue #${issue.id}`, fullURL);
      projectLines.push(markdown.line(`- ${link}`));
    });
  });

  // Check if any lines were actually generated (handles edge case: issues exist but have empty project arrays)
  if (!projectLines.length) {
    return '';
  }

  return heading + _.uniq(projectLines).join('');
};

/**
 * Renders the section which displays the licenses
 * for Licensing issues.
 *
 * @param {LicensingIssue[]} issues - List of licensing issues.
 */
const LicenseSection = (issues: LicensingIssue[]): string => {
  const licenses = issues
    .filter(({ license }) => license)
    .map(({ license }) => markdown.line(`- ${license}`));

  if (!licenses.length) {
    return markdown.lines('', 2) + markdown.line(markdown.bold('No licenses found by FOSSA.'));
  }
  return markdown.lines('', 2) + markdown.h3('Licenses found by FOSSA') + licenses.join('');
};

/**
 * Returns the policy content for the licensing issue.
 *
 * @param {LicensingIssue} issue - A given licensing issue.
 */
const IssueLicensingContent = (issue: LicensingIssue): string => {
  switch (issue.type) {
    case 'policy_conflict':
      return markdown.line(`Your policy disallows dependencies licensed under the ${issue.license} license.`);
    case 'policy_flag':
      return markdown.line(`Your policy has flagged the ${issue.license} license for review.`);
    case 'unconcluded_dependency':
      return markdown.line(
        'This dependency does not have a concluded license but licenses are available. It is recommended that you select the license you are utilizing this dependency under.',
      );
    case 'unlicensed_dependency':
    default:
      return markdown.line('This dependency hasn\'t specified a license. You may need to contact the author to obtain permission to use it.');
  }
};

/**
 * Renders content for LicensingIssues.
 *
 * @param {LicensingIssue[]} issues - List of licensing issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 */
const IssueLicensing = (
  issues: LicensingIssue[],
  location: Location,
  scope: Scope,
): string => {
  const issue = _.head(issues) as LicensingIssue;
  return [
    Header(issue),
    IssueLicensingContent(issue),
    markdown.lines('', 2),
    ViewOnFossaLinks(issues),
    PackageURL(issue.source),
    ProjectLinks(issues, location, scope, Category.Licensing),
    LicenseSection(issues),
    markdown.lines('', 2),
  ].join('');
};

/**
 * Builds the main ticket content for licensing issues,
 * doing so by grouping by issue type.
 *
 * @param {LicensingIssue[]} ungroupedIssues - List of licening issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 */
const LicensingIssuesByType = (
  ungroupedIssues: LicensingIssue[],
  location: Location,
  scope: Scope,
) =>
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(_.groupBy(ungroupedIssues, issue => issue.type), (issues: LicensingIssue[]) => IssueLicensing(issues, location, scope)).join('');

/**
 * Displays the number of discovered vulns.
 *
 * @param {number} issuesCount - The number of vulns issues.
 */
const VulnIssueCount = (issuesCount: number): string =>
  markdown.lines(markdown.bold(`Discovered ${issuesCount} ${plural(issuesCount, 'Vulnerability', 'Vulnerabilities')}:`), 3);

/**
 * Renders the core content for a vulns issue.
 *
 * @param {VulnIssue} issue - A given VulnIssue
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 * @param {string} remediationVersion - Optional override for the remediation line, used when the user picked a specific fix version.
 */
const IssueVulnContent = (
  issue: VulnerabilityIssue,
  location: Location,
  scope: Scope,
  remediationVersion?: string,
): string => {
  let text = markdown.line(`- ${markdown.bold(issue.cve)}`);
  text += markdown.line(`-- Severity: ${_.capitalize(issue.severity)}${issue.cvss ? `(${issue.cvss})` : ''}`);
  if (remediationVersion) {
    text += markdown.line(`-- Remediation: Upgrade to ${remediationVersion}`);
  } else if (issue.remediation && issue.remediation.partialFix === NO_SAFE_VERSION) {
    text += markdown.line(`-- Remediation: No Fix`);
  } else if (issue.remediation && issue.remediation.partialFix !== UNABLE_TO_CALCULATE) {
    text += markdown.line(`-- Remediation: Upgrade to ${issue.remediation.partialFix}`);
  }
  text += markdown.panel(`${issue.cve} Details`, issue.details || 'No details available.');
  text += markdown.lines('', 3);
  text += markdown.line(ProjectLinks([issue], location, scope, Category.Vulnerability));
  text += markdown.line(ViewOnFossaLink(issue.id, issue.url));
  return text;
};

/**
 * Renders content for VulnIssues.
 *
 * @param {VulnIssue[]} issues - Vulnerability issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 * @param {string} remediationVersion - Optional override for the remediation line, used when the user picked a specific fix version.
 */
const IssueVuln = (issues: VulnerabilityIssue[], location: Location, scope: Scope, remediationVersion?: string) => {
  const issue = _.head(issues) as VulnerabilityIssue;
  return [
    Header(issue),
    PackageURL(issue.source),
    VulnIssueCount(issues.length),
    issues.map(vulnIssue => IssueVulnContent(vulnIssue, location, scope, remediationVersion)).join(markdown.line()),
    markdown.line(),
  ].join('');
};

/**
 * Renders the content for a malware issue.
 */
const IssueMalwareContent = (issue: MalwareVulnIssue, location: Location, scope: Scope): string => {
  let text = markdown.line(`- ${markdown.bold(issue.malwareReportName || 'Malicious Dependency')}`);
  if (issue.malwareDescription) {
    text += markdown.line(`-- ${issue.malwareDescription}`);
  }
  if (issue.malwareReportUrl) {
    text += markdown.line(`-- Report: ${issue.malwareReportUrl}`);
  }
  text += markdown.lines('', 3);
  text += markdown.line(ProjectLinks([issue], location, scope, Category.Vulnerability));
  text += markdown.line(ViewOnFossaLink(issue.id, issue.url));
  return text;
};

/**
 * Renders content for malware issues grouped by dependency.
 */
const IssueMalware = (issues: MalwareVulnIssue[], location: Location, scope: Scope) => {
  const issue = _.head(issues) as MalwareVulnIssue;
  return [
    Header(issue),
    PackageURL(issue.source),
    markdown.lines(markdown.bold(`Discovered ${issues.length} Malicious ${plural(issues.length, 'Dependency', 'Dependencies')}:`), 3),
    issues.map(malwareIssue => IssueMalwareContent(malwareIssue, location, scope)).join(markdown.line()),
    markdown.line(),
  ].join('');
};

/**
 * Renders the quality issue details
 *
 * @param {QualityIssue} issue - A quality issue.
 */
const IssueQualityContent = (issue: QualityIssue): string => {
  switch (issue.type) {
    case 'blacklisted_dependency':
      return markdown.line(`Your policy has blocked the ${issue.source.name || issue.source.id} dependency.`);
    case 'outdated_dependency':
      return markdown.line('These dependencies have been flagged as outdated by your policy. This may either be an out of date Major, Minor, or Patch version.');
    case 'risk_empty-package':
      return markdown.line('These dependencies have no reasonable source code and are considered empty.');
    case 'risk_native-code':
      return markdown.line('These dependencies contain compiled executables that are not expected within their ecosystem.');
    case 'risk_abandonware':
    default:
      return markdown.line('These dependencies have not received updates in over two years and may have been abandoned.');
  }
};

/**
 * Renders content for QualityIssues.
 *
 * @param {QualityIssue[]} issues - Quality issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {Scope} scope - The scope of the issues.
 */
const IssueQuality = (issues: QualityIssue[], location: Location, scope: Scope): string => {
  const issue = _.head(issues) as QualityIssue;
  return [
    Header(issue),
    IssueQualityContent(issue),
    markdown.line(),
    PackageURL(issue.source),
    ViewOnFossaLinks(issues),
    ProjectLinks(issues, location, scope, Category.Quality),
  ].join('');
};

/**
 * Renders the footer portion of the ticket content.
 *
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {User} user - window.user, passed in to make unit testing easier.
 */
const Footer = (location: Location, user: User): string => [
  markdown.hr(),
  markdown.line(
    `Generated by ${markdown.link('FOSSA', location.origin)} on ${moment().format('L')}`,
  ),
  markdown.lines(`Ticket generated by FOSSA user: ${user.username || user.email}`, 2),
].join('\n');

/**
 * Parameters for building ticket content.
 *
 * issues - List of issues. For issue details, this typically contains just one issue.
 * issueCategory - The category of issues (Licensing, Vulnerability, or Quality).
 * selectedIssuesCount - Total number of selected issues. For issue details, this is usually 1.
 * location - The browser's location object, used for generating links back to FOSSA.
 * user - The current user information for attribution in the ticket.
 * scope - The scope of the issues.
 * remediationVersion - Optional override for the remediation line on vulnerability issues.
 */
type ContentParams = {
  issues: Issue[];
  issueCategory: Category;
  scope: Scope;
  selectedIssuesCount: number;
  location: Location;
  user: User;
  remediationVersion?: string;
};

/**
 * Main function which takes issues, groups them by source, and builds formatted ticket content
 * based on the issue category. Used for exporting issues to ticketing systems.
 *
 * @param params - Object containing all parameters needed to build ticket content
 * @param params.issues - List of issues. For issue details, this typically contains just one issue
 * @param params.issueCategory - The category of issues (Licensing, Vulnerability, or Quality)
 * @param params.scope - The scope of the issues.
 * @param params.selectedIssuesCount - Total number of selected issues.
 * @param params.location - The browser's location object, used for generating links back to FOSSA
 * @param params.user - The current user information for attribution in the ticket
 * @param params.remediationVersion - Optional override for the remediation line on vulnerability issues
 * @returns Formatted markdown content for the ticket
 */
export const Content = ({
  issues,
  issueCategory,
  selectedIssuesCount,
  location,
  user,
  scope,
  remediationVersion,
}: ContentParams) => {
  let ticketContent = selectedIssuesCount > issues.length
    ? FEMaxCountWarning(selectedIssuesCount)
    : '';
  const issuesByDependencies = _.groupBy(issues, issue => issue.source.id);
  switch (issueCategory) {
    case Category.Licensing:
      ticketContent +=
        // Iterating over a collection. Cannot use Array.map
        // eslint-disable-next-line you-dont-need-lodash-underscore/map
        _.map(issuesByDependencies, (groupedIssues: LicensingIssue[]) => LicensingIssuesByType(groupedIssues, location, scope)).join('\n\n');
      break;

    case Category.Vulnerability:
      // Iterating over a collection. Cannot use Array.map
      // eslint-disable-next-line you-dont-need-lodash-underscore/map
      ticketContent += _.map(issuesByDependencies, (groupedIssues: VulnIssue[]) => {
        // VulnIssue is exactly `vulnerability | malware`, so these two buckets are exhaustive.
        // If a new Vulnerability-category type is ever added it must get a branch here, otherwise
        // it would be silently dropped from the exported ticket.
        const vulnOnly = groupedIssues.filter((i): i is VulnerabilityIssue => i.type === 'vulnerability');
        const malwareOnly = groupedIssues.filter((i): i is MalwareVulnIssue => i.type === 'malware');
        const parts: string[] = [];
        if (vulnOnly.length > 0) {
          parts.push(IssueVuln(vulnOnly, location, scope, remediationVersion));
        }
        if (malwareOnly.length > 0) {
          parts.push(IssueMalware(malwareOnly, location, scope));
        }
        return parts.join('\n\n');
      }).filter(Boolean).join('\n\n');
      break;

    case Category.Quality:
    default:
      ticketContent +=
        // Iterating over a collection. Cannot use Array.map
        // eslint-disable-next-line you-dont-need-lodash-underscore/map
        _.map(issuesByDependencies, (groupedIssues: QualityIssue[]) => IssueQuality(groupedIssues, location, scope)).join('\n\n');
      break;
  }
  ticketContent += Footer(location, user);
  return ticketContent;
};

//  These exports are just so we can unit test them,
//  and they should not be imported by application code.
//  eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
export const _testing = {
  FEMaxCountWarning,
  Title,
  ViewOnFossaLink,
  Header,
  PackageURL,
  ProjectLinks,
  LicenseSection,
  IssueLicensingContent,
  LicensingIssuesByType,
  VulnIssueCount,
  IssueVulnContent,
  IssueVuln,
  IssueMalwareContent,
  IssueMalware,
  IssueQualityContent,
  IssueQuality,
  Footer,
};
